// i18next initialization — синхронная загрузка bundles для TR/BG/EN.
//
// См. .planning/phases/01-foundation/01-RESEARCH.md Pattern 7 (i18n SoT).
// Resources генерируются tools/i18n/build.py из locales/dispatcher/*.yaml.
//
// Phase 1 — eager-load всех 3 bundles (~3KB gzip each). Phase 2 при добавлении
// driver/customer audiences перейдёт на i18next-http-backend lazy load.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Phase 5 Plan 05-01 — billing namespace (6 locales: en/tr/bg + ru/de/fr).
// Only en/tr/bg in main bundle; ru/de/fr are billing-only owner-facing strings
// (owner может выбрать ru/de/fr вручную через future SettingsLanguage extension).
import bgBilling from "../locales/bg/billing.json";
import bgCustomerFacing from "../locales/bg/customer_facing.json";
import bgDispatcher from "../locales/bg/dispatcher.json";
// Phase 5 Plan 05-03 — gdpr namespace (6 locales: en/tr/bg + ru/de/fr).
// ConfirmDelete static landing page consumes this namespace via direct email
// link — locale resolved by browser language or query param.
import bgGdpr from "../locales/bg/gdpr.json";
// Phase 5 Plan 05-04 — settings_legal namespace (en/tr/bg в main bundle;
// ru/de/fr files exist but registered здесь only через main bundle. SettingsLegal
// + SccClickwrap default к en/tr/bg per SUPPORTED_LOCALES).
import bgSettingsLegal from "../locales/bg/settings_legal.json";
import enBilling from "../locales/en/billing.json";
import enCustomerFacing from "../locales/en/customer_facing.json";
import enDispatcher from "../locales/en/dispatcher.json";
import enGdpr from "../locales/en/gdpr.json";
import enSettingsLegal from "../locales/en/settings_legal.json";
// Phase 12 Plan 12-01 — sr namespace (Serbian latinica, ключ 'sr' per locked Q1).
// Значения = placeholder=en (реальный sr-перевод в плане 12-05); полный
// key-parity обязателен для прохождения i18n CI hard-gate.
import srBilling from "../locales/sr/billing.json";
import srCustomerFacing from "../locales/sr/customer_facing.json";
import srDispatcher from "../locales/sr/dispatcher.json";
import srGdpr from "../locales/sr/gdpr.json";
import srSettingsLegal from "../locales/sr/settings_legal.json";
import trBilling from "../locales/tr/billing.json";
import trCustomerFacing from "../locales/tr/customer_facing.json";
import trDispatcher from "../locales/tr/dispatcher.json";
import trGdpr from "../locales/tr/gdpr.json";
import trSettingsLegal from "../locales/tr/settings_legal.json";

export const SUPPORTED_LOCALES = ["en", "tr", "bg", "sr"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "en";

/**
 * Initialize i18next с dispatcher namespace для TR/BG/EN.
 *
 * Idempotent: повторные вызовы возвращают тот же экземпляр (i18next.isInitialized).
 *
 * @param initialLocale - стартовая локаль (обычно из resolveTelegramLocale)
 * @returns initialized i18n instance
 */
export async function initI18n(
  initialLocale: SupportedLocale = DEFAULT_LOCALE,
): Promise<typeof i18n> {
  if (i18n.isInitialized) {
    if (i18n.language !== initialLocale) {
      await i18n.changeLanguage(initialLocale);
    }
    return i18n;
  }

  await i18n.use(initReactI18next).init({
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: "dispatcher",
    ns: ["dispatcher", "customer_facing", "billing", "gdpr", "settings_legal"],
    resources: {
      en: {
        dispatcher: enDispatcher,
        customer_facing: enCustomerFacing,
        billing: enBilling,
        gdpr: enGdpr,
        settings_legal: enSettingsLegal,
      },
      tr: {
        dispatcher: trDispatcher,
        customer_facing: trCustomerFacing,
        billing: trBilling,
        gdpr: trGdpr,
        settings_legal: trSettingsLegal,
      },
      bg: {
        dispatcher: bgDispatcher,
        customer_facing: bgCustomerFacing,
        billing: bgBilling,
        gdpr: bgGdpr,
        settings_legal: bgSettingsLegal,
      },
      // Phase 12 — sr (Serbian latinica). Values=en placeholder; fallbackLng:"en"
      // обеспечивает цепочку sr→en пока 12-05 не подменит реальный перевод.
      sr: {
        dispatcher: srDispatcher,
        customer_facing: srCustomerFacing,
        billing: srBilling,
        gdpr: srGdpr,
        settings_legal: srSettingsLegal,
      },
    },
    interpolation: {
      // React уже escapes — двойной escape ломает HTML entities в Bulgarian.
      escapeValue: false,
    },
    // i18next defaults keySeparator "." → nested JSON ключи доступны как
    // t("welcome.owner.bot") (что совпадает с YAML SoT key path).
    keySeparator: ".",
    nsSeparator: ":",
  });

  return i18n;
}
